import React, { useEffect, useRef, useState } from "react";
import { api, JERSEY_STYLES, fmt } from "@/api";
import { BigTimer } from "@/components/Shared";

const nowClock = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtClock = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch (e) { return ""; }
};

function RiderRow({ jersey, name, total, hide }) {
  const s = JERSEY_STYLES[jersey] || JERSEY_STYLES.black;
  return (
    <div className="flex items-center gap-4 p-3" style={{ border: "3px solid #2a2a2a", background: "#141414" }}>
      <div
        className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center font-black text-3xl shrink-0"
        style={{ background: s.bg, color: s.fg, border: `4px solid ${s.border === s.bg ? "#ffffff" : s.border}` }}
      >
        {s.label[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-black uppercase truncate text-3xl sm:text-4xl leading-none text-white">{name}</div>
        <div className="font-display uppercase tracking-[0.2em] text-xs sm:text-sm mt-1" style={{ color: s.bg === "#FFFFFF" ? "#cccccc" : s.bg }}>
          {s.label} jersey
        </div>
      </div>
      {!hide && (
        <div className="text-right shrink-0">
          <div className="font-score text-6xl sm:text-7xl leading-none text-[#FFD700]">{fmt(total)}</div>
          <div className="font-display text-[10px] tracking-[0.25em] uppercase text-gray-500">Best 2</div>
        </div>
      )}
    </div>
  );
}

export default function AnnouncerScreen() {
  const [data, setData] = useState({ live: null, on_deck: null, event_live: null });
  const [plan, setPlan] = useState(null);
  const [clock, setClock] = useState(nowClock());
  const livePoll = useRef(null);
  const planPoll = useRef(null);

  useEffect(() => {
    const loadLive = () => api.live().then(setData).catch(() => {});
    const loadPlan = () => api.schedulePlan().then(setPlan).catch(() => {});
    loadLive();
    loadPlan();
    livePoll.current = setInterval(loadLive, 2000);
    planPoll.current = setInterval(loadPlan, 5000);
    const c = setInterval(() => setClock(nowClock()), 5000);
    return () => { clearInterval(livePoll.current); clearInterval(planPoll.current); clearInterval(c); };
  }, []);

  const live = data.live;
  const onDeck = data.on_deck;
  const upcoming = (plan?.heats || []).filter((h) => ["live", "on_deck", "upcoming"].includes(h.status));
  const marquee = upcoming.map((h) => `${fmtClock(h.scheduled_at)} · ${h.division_name} ${h.name}${h.is_final ? " (FINAL)" : ""}`);

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto" style={{ background: "#0A0A0A", color: "#fff" }} data-testid="announcer-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b-4" style={{ borderColor: "#E63946" }}>
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full bg-[#E63946] announcer-pulse" />
          <span className="font-display font-black uppercase tracking-[0.3em] text-lg sm:text-2xl">Pounders Fest · Live</span>
        </div>
        <div className="font-score text-3xl sm:text-4xl text-[#FFD700]" data-testid="announcer-clock">{clock}</div>
      </div>

      {data.event_live === false ? (
        <div className="flex flex-col items-center justify-center text-center px-6" style={{ minHeight: "70vh" }} data-testid="announcer-standby">
          <div className="font-score text-6xl sm:text-8xl leading-none">Standing By</div>
          <p className="font-display uppercase tracking-[0.3em] text-gray-400 mt-6 text-sm sm:text-base">Heats begin shortly — check the schedule</p>
        </div>
      ) : (
        <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6" style={{ paddingBottom: 96 }}>
          {/* NOW */}
          <div className="lg:col-span-2" data-testid="announcer-now">
            <div className="font-display font-black uppercase tracking-[0.3em] text-[#E63946] text-sm sm:text-base mb-2">● Now In The Water</div>
            {live ? (
              <>
                <div className="font-display uppercase tracking-[0.2em] text-gray-400 text-sm sm:text-lg">
                  {live.division_name}{live.round_name ? ` · ${live.round_name}` : ""}
                </div>
                <h1 className="font-display font-black uppercase tracking-tight leading-none text-5xl sm:text-7xl mb-4">{live.name}</h1>
                <BigTimer timer={live.timer} large />
                {live.hide_scores ? (
                  <div className="mt-4 border-4 border-[#FFD700] p-4 text-center">
                    <div className="font-display font-black uppercase tracking-tight text-2xl text-[#FFD700]">🔒 Final In Progress</div>
                    <p className="font-display text-sm text-gray-300 mt-1 uppercase tracking-widest">Champion revealed at the ceremony</p>
                  </div>
                ) : null}
                <div className="mt-4 space-y-3" data-testid="announcer-now-riders">
                  {(live.hide_scores ? live.riders : live.leaderboard).slice(0, 8).map((r) => (
                    <RiderRow key={r.rider_id} jersey={r.jersey} name={r.name} total={r.total} hide={live.hide_scores} />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-center border-4 border-[#2a2a2a] p-10" style={{ minHeight: "40vh" }} data-testid="announcer-no-live">
                <div className="font-score text-5xl sm:text-7xl leading-none">Between Heats</div>
                <p className="font-display uppercase tracking-[0.3em] text-gray-400 mt-4 text-sm">Next heat loading up</p>
              </div>
            )}
          </div>

          {/* NEXT */}
          <div data-testid="announcer-next">
            <div className="font-display font-black uppercase tracking-[0.3em] text-[#FFD700] text-sm sm:text-base mb-2">On Deck — Next Up</div>
            {onDeck ? (
              <div className="border-4 border-[#FFD700] p-4">
                <div className="font-display uppercase tracking-[0.2em] text-gray-400 text-xs sm:text-sm">
                  {onDeck.division_name}{onDeck.round_name ? ` · ${onDeck.round_name}` : ""}
                </div>
                <h2 className="font-display font-black uppercase tracking-tight leading-none text-4xl sm:text-5xl mb-4">{onDeck.name}</h2>
                <div className="space-y-2">
                  {onDeck.riders.map((r) => {
                    const s = JERSEY_STYLES[r.jersey] || JERSEY_STYLES.black;
                    return (
                      <div key={r.rider_id} className="flex items-center gap-3 p-2" style={{ background: "#141414", border: "2px solid #2a2a2a" }}>
                        <div className="w-12 h-12 flex items-center justify-center font-black text-lg shrink-0"
                          style={{ background: s.bg, color: s.fg, border: `3px solid ${s.border === s.bg ? "#fff" : s.border}` }}>{s.label[0]}</div>
                        <span className="font-display font-black uppercase truncate text-2xl">{r.name}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="font-display uppercase tracking-widest text-xs text-gray-400 mt-4">Riders — grab your jersey &amp; report to the marshal</p>
              </div>
            ) : (
              <div className="border-4 border-[#2a2a2a] p-6 text-center" data-testid="announcer-no-next">
                <div className="font-score text-4xl">—</div>
                <p className="font-display uppercase tracking-widest text-xs text-gray-500 mt-2">No heat on deck</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rolling schedule marquee */}
      {marquee.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 overflow-hidden border-t-4 py-3" style={{ background: "#141414", borderColor: "#E63946" }} data-testid="announcer-marquee">
          <div className="announcer-marquee">
            {[0, 1].map((dup) => (
              <span key={dup} className="inline-flex items-center">
                {marquee.map((m, i) => (
                  <span key={`${dup}-${i}`} className="font-display font-black uppercase tracking-widest text-lg sm:text-2xl px-8 text-white">
                    {m}<span className="text-[#E63946] px-4">•</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
